import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';

import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { AlertDialog } from '../dialogs/alert-dialog';
import { EnvProperty } from '../models/envproperty';
import { LogGradimento } from '../models/loggradimento';
import { Referto } from '../models/referto';
import { ServiceRequest } from '../models/servicerequest';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  }),
  withCredentials: true
};

export interface SessionId {
  sessionId?: string;
}

export interface UrlRef {
  url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BackendService {

  private static _dialog: MatDialog;

  public sessionId: string = null;

  private static loadingSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private dialog: MatDialog
  ) {
    BackendService._dialog = dialog;
  }

  public static setLoading(value: boolean) {
    this.loadingSubject.next(value);
  }

  public static getLoading(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  private handleError(error: HttpErrorResponse) {
    BackendService.setLoading(false);
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
      errorMessage = error.error.message;
    } else {
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error.message}`);
      errorMessage = error.error.message;
    }
    if (errorMessage.toLowerCase().includes(`could not do a physical-order read to fetch next row`)) {
      errorMessage = `Errore temporaneo nella ricerca del referto: riprovare più tardi`;
    }
    BackendService._dialog.open(AlertDialog, {
      data: { message: `${errorMessage}` }
    });
    return throwError(
      'Something bad happened; please try again later.');
  }

  getSessionId(): Observable<string> {
    return new Observable<string>(observer => {
      if (this.sessionId != null) {
        observer.next(this.sessionId);
      } else {
        this.http.get(this.configService.getBackendUrl(`/getSessionId`), httpOptions).pipe(
          retry(0),
          catchError(this.handleError)
        ).subscribe((result: SessionId) => {
          this.sessionId = result.sessionId;
          console.log(`RECEIVED SESSION ID ${this.sessionId}`);
          observer.next(this.sessionId);
        });
      }
    });
  }

  getEnvProperty(name: string): Observable<EnvProperty> {
    return this.http.get<EnvProperty>(this.configService.getBackendUrl(`/getEnvProperty?name=${name}`), httpOptions).pipe(
      retry(0),
      catchError(this.handleError)
    );
  }

  findReferti(request: ServiceRequest): Observable<Referto[]> {
    return new Observable<Referto[]>(observer => {
      this.http.post(this.configService.getBackendUrl('/findReferti'), request, httpOptions).pipe(
        retry(0), // retry a failed request up to 3 times
        catchError(this.handleError) // then handle the error
      ).subscribe((results: Referto[]) => observer.next(results));
    });
  }

  verificatRefertoMMN(username: string, pin: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.http.get(this.configService.getBackendUrl(`/verificaRefertoMMN?username=${username}&pin=${pin}`), httpOptions).pipe(
        retry(0),
        catchError(this.handleError)
      ).subscribe((result: boolean) => observer.next(result));
    });
  }

  cancellaRefertoMMN(username: string, pin: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.http.get(this.configService.getBackendUrl(`/cancellaRefertoMMN?username=${username}&pin=${pin}`), httpOptions).pipe(
        retry(0),
        catchError(this.handleError)
      ).subscribe((result: boolean) => observer.next(result));
    });
  }

  getUrlImmaginiReferto(scope: string, codPaziente: string, codSede: string, referto: Referto): Observable<string> {
    return new Observable<string>(observer => {
      this.http.get(this.configService.getBackendUrl(`/getUrlImmaginiReferto?oper=referto&codSede=${codSede}&uid=${referto.repUid}&scope=${scope}&codPaziente=${codPaziente}`), httpOptions).pipe(
        retry(0),
        catchError(this.handleError)
      ).subscribe((result: UrlRef) => observer.next(result.url));
    });
  }

  insertLogGradimento(log: LogGradimento): Observable<void> {
    return this.http.post<void>(this.configService.getBackendUrl(`/insertLogGradimento`), log, httpOptions).pipe(
      retry(0),
      catchError(this.handleError)
    );
  }

}
